// frontend/src/components/SoundManager.jsx
import { useState, useEffect } from 'react';
import api from '../services/api';
import { useNotificationSound } from '../hooks/useNotificationSound';

export default function SoundManager() {
    const [sounds, setSounds] = useState([]);
    const [uploading, setUploading] = useState(null);
    const [testingSound, setTestingSound] = useState(null);
    const { loadSounds, playSound } = useNotificationSound();

    const serviceTypes = [
        { key: 'cleaning', name: 'Higienização', icon: '🧹', color: 'success' },
        { key: 'housekeeper', name: 'Governanta', icon: '🏨', color: 'primary' },
        { key: 'laundry', name: 'Lavanderia', icon: '👔', color: 'info' },
        { key: 'maintenance', name: 'Manutenção', icon: '🔧', color: 'warning' }
    ];

    useEffect(() => {
        fetchSounds();
    }, []);

    const fetchSounds = async () => {
        try {
            const response = await api.get('/sounds');
            setSounds(response.data);
        } catch (error) {
            console.error('Erro ao carregar sons:', error);
        }
    };

    const handleFileUpload = async (serviceType, file) => {
        if (!file) return;

        // Validação no frontend
        if (!file.type.startsWith('audio/')) {
            alert('Por favor, selecione um arquivo de áudio (.mp3, .wav, .ogg)');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Arquivo muito grande. Máximo: 5MB');
            return;
        }

        setUploading(serviceType);

        try {
            const formData = new FormData();
            formData.append('sound', file);
            formData.append('service_type', serviceType);

            await api.post('/sounds', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            alert('Som enviado com sucesso!');
            fetchSounds();
            loadSounds(); // Recarregar sons no hook
        } catch (error) {
            console.error('Erro ao enviar som:', error);
            alert('Erro ao enviar som: ' + (error.response?.data?.error || error.message));
        } finally {
            setUploading(null);
        }
    };

    const handleDeleteSound = async (soundId) => {
        if (!window.confirm('Tem certeza que deseja remover este som? O som padrão será usado.')) {
            return;
        }

        try {
            await api.delete(`/sounds/${soundId}`);
            alert('Som removido com sucesso!');
            fetchSounds();
            loadSounds(); // Recarregar sons no hook
        } catch (error) {
            console.error('Erro ao remover som:', error);
            alert('Erro ao remover som: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleTestSound = (serviceType) => {
        setTestingSound(serviceType);
        playSound(serviceType);
        setTimeout(() => setTestingSound(null), 2000);
    };

    const getSoundForService = (serviceType) => {
        return sounds.find(s => s.service_type === serviceType);
    };

    return (
        <div className="container-fluid py-4">
            <h3 className="mb-4">
                <i className="bi bi-megaphone me-2"></i>
                Gerenciar Sons de Notificação
            </h3>

            <p className="text-muted mb-4">
                Configure sons personalizados para cada tipo de serviço.
                Formatos aceitos: MP3, WAV, OGG (máx. 5MB).
            </p>

            <div className="row g-4">
                {serviceTypes.map(service => {
                    const currentSound = getSoundForService(service.key);
                    const isUploading = uploading === service.key;
                    const isTesting = testingSound === service.key;

                    return (
                        <div key={service.key} className="col-md-6">
                            <div className={`card border-${service.color} h-100`}>
                                <div className={`card-header bg-${service.color} bg-opacity-10 d-flex align-items-center`}>
                                    <span className="fs-4 me-2">{service.icon}</span>
                                    <h5 className={`mb-0 text-${service.color}`}>{service.name}</h5>
                                </div>

                                <div className="card-body">
                                    {currentSound ? (
                                        <>
                                            <div className="d-flex align-items-center mb-3">
                                                <i className="bi bi-music-note-beamed text-success me-2"></i>
                                                <div className="flex-grow-1">
                                                    <strong>{currentSound.sound_name}</strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        {(currentSound.file_size / 1024).toFixed(1)} KB
                                                        {currentSound.uploaded_by_user && (
                                                            <> • Enviado por {currentSound.uploaded_by_user.full_name}</>
                                                        )}
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    className={`btn btn-outline-${service.color} btn-sm`}
                                                    onClick={() => handleTestSound(service.key)}
                                                    disabled={isTesting}
                                                >
                                                    <i className={`bi bi-${isTesting ? 'volume-up-fill' : 'play-circle'} me-1`}></i>
                                                    {isTesting ? 'Tocando...' : 'Testar'}
                                                </button>

                                                <label className={`btn btn-${service.color} btn-sm`}>
                                                    <i className="bi bi-arrow-repeat me-1"></i>
                                                    Trocar Som
                                                    <input
                                                        type="file"
                                                        accept="audio/*"
                                                        className="d-none"
                                                        onChange={(e) => handleFileUpload(service.key, e.target.files[0])}
                                                        disabled={isUploading}
                                                    />
                                                </label>

                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleDeleteSound(currentSound.id)}
                                                >
                                                    <i className="bi bi-trash me-1"></i>
                                                    Remover
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-center p-3 mb-3 bg-light rounded">
                                                <i className="bi bi-music-note text-muted fs-1"></i>
                                                <p className="text-muted mb-0">Usando som padrão</p>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    className={`btn btn-outline-${service.color} btn-sm`}
                                                    onClick={() => handleTestSound(service.key)}
                                                    disabled={isTesting}
                                                >
                                                    <i className={`bi bi-${isTesting ? 'volume-up-fill' : 'play-circle'} me-1`}></i>
                                                    {isTesting ? 'Tocando...' : 'Testar Padrão'}
                                                </button>

                                                <label className={`btn btn-${service.color} btn-sm flex-grow-1`}>
                                                    {isUploading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                                            Enviando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-upload me-1"></i>
                                                            Enviar Som Personalizado
                                                        </>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept="audio/*"
                                                        className="d-none"
                                                        onChange={(e) => handleFileUpload(service.key, e.target.files[0])}
                                                        disabled={isUploading}
                                                    />
                                                </label>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="alert alert-info mt-4">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Dica:</strong> Os sons personalizados são reproduzidos automaticamente quando novos alertas aparecem no painel de serviços.
            </div>
        </div>
    );
}
